import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import SearchBar from './SearchBar';

// Simple Bell icon component (since we don't have Lucide React)
const BellIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
    />  </svg>
);

// Hamburger menu icon
const MenuIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 6h16M4 12h16M4 18h16"
    />
  </svg>
);

// Close (X) icon
const CloseIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M6 18L18 6M6 6l12 12"
    />
  </svg>
);

// Search icon
const SearchIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="m21 21-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
    />
  </svg>
);

interface DropdownMenuProps {
  trigger: React.ReactNode;
  children: React.ReactNode;
}

const DropdownMenu: React.FC<DropdownMenuProps> = ({ trigger, children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      <div onClick={() => setIsOpen(!isOpen)} className="cursor-pointer">
        {trigger}
      </div>
      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />          {/* Dropdown */}
          <div className="absolute right-0 mt-2 w-56 bg-paper border border-parchment-300 rounded-md shadow-paper-lg z-50">
            <div className="py-1">
              {React.Children.map(children, child => {
                if (React.isValidElement(child)) {
                  return React.cloneElement(child, { onClose: () => setIsOpen(false) } as any);
                }
                return child;
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

interface DropdownItemProps {
  children: React.ReactNode;
  onClick?: () => void;
  href?: string;
  onClose?: () => void;
}

const DropdownItem: React.FC<DropdownItemProps> = ({ children, onClick, href, onClose }) => {
  const handleClick = () => {
    if (onClick) {
      onClick();
    }
    if (onClose) {
      onClose();
    }
  };
  const content = (
    <div 
      className="px-4 py-2 text-sm text-custom-blue-800 hover:bg-parchment-100 hover:text-custom-blue-900 cursor-pointer transition-all duration-200"
      onClick={handleClick}
    >
      {children}
    </div>
  );

  if (href) {
    return (
      <Link to={href} onClick={onClose}>
        {content}
      </Link>
    );
  }

  return content;
};

const Header: React.FC = () => {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const [notifications] = useState(0); // Mock notification count
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);

  const isActivePage = (path: string) => location.pathname === path;

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const toggleSearch = () => {
    setIsSearchExpanded(!isSearchExpanded);
  };

  if (!user) {
    // Show simplified header for non-authenticated users
    return (
      <header className="bg-custom-blue-800 border-b-2 border-custom-blue-900 relative z-50 shadow-lg">
        <div className="container mx-auto flex items-center justify-between h-16 px-4 lg:px-6">
          {/* Left: Logo */}
          <div className="flex items-center space-x-4">
            <Link to="/feed" className="flex items-center font-semibold text-white space-x-3 hover:text-parchment-100 transition-colors">
              <img 
                src="/logo200.png" 
                alt="The Myth Dimension Logo" 
                className="h-8 w-8 lg:h-10 lg:w-10 rounded-md object-cover"
              />
              <span className="text-lg lg:text-xl">The Myth Dimension</span>
            </Link>
          </div>

          {/* Center: Search */}
          <div className="hidden md:flex flex-1 max-w-md mx-8">
            <SearchBar />
          </div>

          {/* Right: Login Button */}
          <div className="flex items-center space-x-4">
            <div className="md:hidden">
              <button 
                onClick={toggleSearch}
                className="text-white hover:text-parchment-100 transition-colors p-2"
              >
                <SearchIcon className="h-6 w-6" />
              </button>
            </div>
            <Link 
              to="/signin"
              className="bg-custom-blue-600 hover:bg-custom-blue-700 text-white px-4 py-2 rounded-md transition-colors font-medium"
            >
              Sign In
            </Link>
          </div>
        </div>

        {/* Mobile Search Expanded */}
        {isSearchExpanded && (
          <div className="md:hidden bg-custom-blue-700 px-4 py-3 border-t border-custom-blue-600">
            <SearchBar />
          </div>
        )}
      </header>
    );
  }

  return (
    <header className="bg-custom-blue-800 border-b-2 border-custom-blue-900 relative z-50 shadow-lg">
      {/* Main Header */}
      <div className="container mx-auto flex items-center justify-between h-16 px-4 lg:px-6">
        {/* Left: Logo and Mobile Menu Button */}
        <div className="flex items-center space-x-3">
          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 hover:bg-custom-blue-700 rounded-md transition-colors"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <CloseIcon className="h-5 w-5 text-white" />
            ) : (
              <MenuIcon className="h-5 w-5 text-white" />
            )}
          </button>

          {/* Logo */}
          <Link 
            to="/feed" 
            className="flex items-center font-semibold text-white space-x-3"
            onClick={closeMobileMenu}
          >
            <img 
              src="/logo200.png" 
              alt="The Myth Dimension Logo" 
              className="h-8 w-8 lg:h-10 lg:w-10 rounded-md object-cover"
            />
            <span className="text-lg lg:text-xl">The Myth Dimension</span>
          </Link>
        </div>

        {/* Center: Desktop Search Bar */}
        <div className="hidden lg:flex flex-1 max-w-lg mx-8">
          <SearchBar />
        </div>

        {/* Right: Desktop Navigation + Mobile Icons */}
        <div className="flex items-center space-x-2 lg:space-x-6">
          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-6">
            <Link 
              to="/feed" 
              className={`text-sm font-medium transition-colors ${
                isActivePage('/feed') 
                  ? 'text-white' 
                  : 'text-custom-blue-200 hover:text-white'
              }`}
            >
              My Feed
            </Link>
            <Link 
              to="/projects" 
              className={`text-sm font-medium transition-colors ${
                isActivePage('/projects') 
                  ? 'text-white' 
                  : 'text-custom-blue-200 hover:text-white'
              }`}
            >
              My Projects
            </Link>
            <Link 
              to="/pending-posts" 
              className={`text-sm font-medium transition-colors ${
                isActivePage('/pending-posts') 
                  ? 'text-white' 
                  : 'text-custom-blue-200 hover:text-white'
              }`}
            >
              Post Drafts
            </Link>
          </nav>

          {/* Mobile Search Button */}
          <button
            onClick={toggleSearch}
            className="lg:hidden p-2 hover:bg-custom-blue-700 rounded-full transition-colors"
            aria-label="Search"
          >
            <SearchIcon className="h-5 w-5 text-custom-blue-200" />
          </button>

          {/* Notification Bell */}
          <DropdownMenu
            trigger={
              <button className="relative p-2 hover:bg-custom-blue-700 rounded-full transition-colors">
                <BellIcon className="h-5 w-5 text-custom-blue-200" />
                {notifications > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full min-w-[1.25rem] h-5 flex items-center justify-center px-1">
                    {notifications > 99 ? '99+' : notifications}
                  </span>
                )}
              </button>
            }
          >
            <DropdownItem>
              <div className="text-custom-blue-700">No new notifications</div>
            </DropdownItem>
          </DropdownMenu>

          {/* Profile Dropdown */}
          <DropdownMenu
            trigger={
              <button className="h-8 w-8 rounded-full ring-offset-background focus:outline-none focus:ring-2 focus:ring-custom-blue-300 focus:ring-offset-2">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={user.picture} alt="Profile" />
                  <AvatarFallback className="text-sm bg-custom-blue-600 text-white">
                    {user.name.substring(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
              </button>
            }
          >
            <DropdownItem href={`/@${user.handle}`}>
              My Profile
            </DropdownItem>
            <DropdownItem href="/settings">
              Settings
            </DropdownItem>
            <DropdownItem onClick={() => signOut()}>
              Logout
            </DropdownItem>
          </DropdownMenu>
        </div>
      </div>

      {/* Mobile Search Bar (Expanded) */}
      {isSearchExpanded && (
        <div className="lg:hidden bg-custom-blue-700 border-t border-custom-blue-600 px-4 py-3">
          <div className="flex items-center space-x-3">
            <div className="flex-1">
              <SearchBar />
            </div>
            <button
              onClick={toggleSearch}
              className="p-2 hover:bg-custom-blue-600 rounded-full transition-colors"
              aria-label="Close search"
            >
              <CloseIcon className="h-5 w-5 text-custom-blue-200" />
            </button>
          </div>
        </div>
      )}

      {/* Mobile Navigation Menu */}
      {isMobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
            onClick={closeMobileMenu}
          />
          
          {/* Mobile Menu */}
          <div className="absolute top-full left-0 right-0 bg-custom-blue-800 border-t border-custom-blue-600 shadow-lg z-50 lg:hidden">
            <nav className="py-4">
              <Link 
                to="/feed" 
                className={`block px-6 py-3 text-base font-medium transition-colors ${
                  isActivePage('/feed') 
                    ? 'text-white bg-custom-blue-700' 
                    : 'text-custom-blue-200 hover:text-white hover:bg-custom-blue-700'
                }`}
                onClick={closeMobileMenu}
              >
                My Feed
              </Link>
              <Link 
                to="/projects" 
                className={`block px-6 py-3 text-base font-medium transition-colors ${
                  isActivePage('/projects') 
                    ? 'text-white bg-custom-blue-700' 
                    : 'text-custom-blue-200 hover:text-white hover:bg-custom-blue-700'
                }`}
                onClick={closeMobileMenu}
              >
                My Projects
              </Link>
              <Link 
                to="/pending-posts" 
                className={`block px-6 py-3 text-base font-medium transition-colors ${
                  isActivePage('/pending-posts') 
                    ? 'text-white bg-custom-blue-700' 
                    : 'text-custom-blue-200 hover:text-white hover:bg-custom-blue-700'
                }`}
                onClick={closeMobileMenu}
              >
                Post Drafts
              </Link>
            </nav>
          </div>
        </>
      )}
    </header>
  );
};

export default Header;
